import { useEffect, useRef } from "react";

import { type ComponentName, useAnalytics } from "@btnet/utilities-analytics";

/**
 * Emits a `close_modal` analytics event once when a modal closes.
 *
 * A modal closes two different ways and neither alone is sufficient:
 *
 * - `visible` flips true -> false while the component stays mounted. This is
 *   what v1's instrumentation watches (`componentDidUpdate` in `BTModal`).
 * - The component unmounts while still `visible`. 265 of BTModal's 406 call
 *   sites pass a bare `visible` — permanently true — and close by unmounting,
 *   the bill detail modal among them. v1 misses every one of these, which is
 *   why a bill close produced a `route_change` with no preceding click.
 *
 * A modal that hides and then unmounts hits both, but cannot double-report:
 * the transition sets `visibleRef` false, and the unmount cleanup only emits
 * while it is true. That single ref is what keeps the two paths exclusive — an
 * additional "already reported" flag was tried and proved unreachable.
 *
 * No-ops entirely without `componentName`: the event is opt-in per modal, since
 * an unnamed close cannot tell the warehouse which modal it came from.
 */
export function useModalCloseAnalytics(
    visible: boolean,
    componentName: ComponentName | undefined,
): void {
    const { trackAnalytics } = useAnalytics();

    // Read inside the effects rather than listed as dependencies: re-running on
    // a new trackAnalytics identity would re-fire the close.
    const trackRef = useRef(trackAnalytics);
    useEffect(() => {
        trackRef.current = trackAnalytics;
    });

    const nameRef = useRef(componentName);
    useEffect(() => {
        nameRef.current = componentName;
    });

    const visibleRef = useRef(visible);

    const emitClose = (): void => {
        const name = nameRef.current;
        if (name === undefined) {
            return;
        }
        trackRef.current({
            eventType: "click",
            eventName: "close_modal",
            componentType: "modal",
            componentName: name,
        });
    };

    // The visible true -> false transition.
    useEffect(() => {
        if (visibleRef.current && !visible) {
            emitClose();
        }
        visibleRef.current = visible;
        // emitClose is deliberately not a dependency: it reads everything it
        // needs through refs, and listing it would re-run this on every render.
    }, [visible]);

    // Unmount while still open. Empty deps so the cleanup runs only on unmount,
    // not on every visible change.
    useEffect(() => {
        return () => {
            if (visibleRef.current) {
                emitClose();
            }
        };
    }, []);
}
